import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Loader2, Tag } from 'lucide-react';
import api from '@/utils/api';
import { showError, showSuccess } from '@/utils/toast';

const emptySource = { name: '', description: '', is_active: true };

const LeadSourceManager = ({ readOnly = false }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySource);

  useEffect(() => {
    fetchSources().finally(() => setLoading(false));
  }, []);

  const fetchSources = async () => {
    try {
      const res = await api.get('/lead-sources');
      setSources(res.data || []);
    } catch (e) {
      showError('Failed to load lead sources');
    }
  };

  const startNew = () => { setForm(emptySource); setEditing('new'); };
  const startEdit = (s) => { setForm({ ...emptySource, ...s }); setEditing(s.id); };
  const cancelEdit = () => { setEditing(null); setForm(emptySource); };

  const handleSave = async () => {
    if (!form.name?.trim()) { showError('Source name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description || null, is_active: !!form.is_active };
      if (editing === 'new') {
        await api.post('/lead-sources', payload);
        showSuccess('Lead source created');
      } else {
        await api.put(`/lead-sources/${editing}`, payload);
        showSuccess('Lead source updated');
      }
      await fetchSources();
      cancelEdit();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead source?')) return;
    try {
      await api.delete(`/lead-sources/${id}`);
      showSuccess('Lead source deleted');
      fetchSources();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="lead-source-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Tag size={20} /> Lead Sources
          </h2>
          <p className="text-sm text-muted-foreground">Channels where leads come from (Website, Walk-in, DSA, etc.).</p>
        </div>
        {!readOnly && editing === null && (
          <button
            data-testid="add-source-button"
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <Plus size={18} /> Add Source
          </button>
        )}
      </div>

      {editing !== null && !readOnly && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="source-form">
          <h3 className="font-semibold mb-4 text-foreground">{editing === 'new' ? 'New Lead Source' : 'Edit Lead Source'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                data-testid="source-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer h-full pt-7">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm">Active</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                rows="2"
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={cancelEdit} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">
              <X size={16} className="inline mr-1" /> Cancel
            </button>
            <button
              data-testid="save-source-button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving && <Loader2 className="animate-spin" size={16} />}
              Save
            </button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Description</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {!readOnly && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sources.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 3 : 4} className="px-4 py-8 text-center text-muted-foreground">
                  No lead sources yet.
                </td>
              </tr>
            ) : (
              sources.map((s) => (
                <tr key={s.id} data-testid={`source-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.description || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${s.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {s.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`edit-source-${s.id}`} onClick={() => startEdit(s)} className="p-2 rounded-lg hover:bg-muted">
                          <Edit3 size={16} className="text-muted-foreground" />
                        </button>
                        <button data-testid={`delete-source-${s.id}`} onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-destructive/10">
                          <Trash2 size={16} className="text-destructive" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadSourceManager;
